import React, { useContext, useEffect, useState, useCallback } from "react"
import PropTypes from "prop-types"
const deepEqual = require("react-fast-compare")

import { AppSettingConfigContext } from "../../config/AppSettingsConfigProvider"
import { getObjectsList } from "../../Loaders"
import { logToServer } from "../../ErrorBoundary"
import LTPMDash from "../../shared_components/graph_dashboards/LTPMDash"
import PageLoadingPanel from "../../shared_components/widgets/PageLoadingPanel"
import LiveReactorSelector, {
  doCeilTwoDecimalRound,
} from "../../shared_components/graph_dashboards/LTPMDash/LiveReactorSelector"
import PageErrorPanel from "../../shared_components/widgets/PageErrorPanel"

/*
  Function props passed are cached via useCallback
  to prevent unnecessary re-renders of RunsContainer when this component re-renders.
*/

export default function LTPMDashboard({
  queryParams,
  doFilterDefaultRuns,
  baseRunidUrl,
  LIVE_DASH_SELECTIVE_LOADING = false,
  intl,
}) {
  const appConfig = useContext(AppSettingConfigContext)

  const [run, setRun] = useState({})
  const [actions, setActions] = useState([])
  const [commentsLoading, setCommentsLoading] = useState(false)
  const [error, setError] = useState(false)
  const [runs, setRuns] = useState([])
  const [options, setOptions] = useState([])
  const [firstRunLoadPending, setFirstRunLoadPending] = useState(true)
  const [didInitialUserQuery, setDidInitialUserQuery] = useState(false)
  const [isQueryingRunIDs, setIsQueryingRunIDs] = useState(false)
  const [runQuery, setRunQuery] = useState({ ...queryParams, sources: [] })
  const updateRunQuery = useCallback(
    (field, val) =>
      setRunQuery((prev) => ({
        ...prev,
        [field]: val,
      })),
    [],
  )

  useEffect(() => {
    doLoadRuns()
  }, [runQuery])

  const doLoadRuns = () => {
    setIsQueryingRunIDs(true)
    const cutoffQuery = firstRunLoadPending || !runQuery.cutoff ? "" : `&cutoff=${runQuery.cutoff}`
    const sourceQuery =
      runQuery.sources.length > 0 ? `&sources=${encodeURIComponent(runQuery.sources.join(","))}` : ""
    const url = `${baseRunidUrl}${cutoffQuery}${sourceQuery}`
    getObjectsList(url)
      .then((runArr) => {
        setRuns(runArr.map((r) => r.id))
        if (
          !firstRunLoadPending &&
          !didInitialUserQuery &&
          (!LIVE_DASH_SELECTIVE_LOADING || runQuery.userLoad)
        )
          setDidInitialUserQuery(true)
        if (firstRunLoadPending) {
          setFirstRunLoadPending(false)
          setOptions(runArr)
          if (runArr.length === 0) {
            !didInitialUserQuery && setDidInitialUserQuery(true)
          } else {
            const initialUserPicks = doFilterDefaultRuns(runArr)
            let info = {}
            if (LIVE_DASH_SELECTIVE_LOADING) {
              const minDay = doCeilTwoDecimalRound(Math.min(...initialUserPicks.map((r) => r.cutoff_day)))
              if (minDay > runQuery.cutoff) info["cutoff"] = minDay
            } else {
              info["sources"] = initialUserPicks.map((r) => r.source)
            }
            setRunQuery((prev) => ({
              ...prev,
              ...info,
            }))
          }
        }
        !run?.id && setCommentsLoading(runArr && runArr.length > 0)
        setError(false)
        setIsQueryingRunIDs(false)
      })
      .catch((err) => {
        console.error(err)
        logToServer(err)
        setError(true)
        setIsQueryingRunIDs(false)
      })
  }

  const checkRunInfoUpdate = useCallback((url, metric, errMetric, currVal) => {
    getObjectsList(url)
      .then((data) => {
        const latestInfo = {}
        data.forEach((run) => {
          latestInfo[run.run] = run["latest"]
        })
        if (!deepEqual(currVal, latestInfo)) {
          return { [metric]: latestInfo }
        }
      })
      .catch((err) => {
        console.error(err)
        logToServer(err)
        return { [errMetric]: err }
      })
  }, [])

  const checkForRunUpdate = useCallback((runArr, live_graphs, state) => {
    let tags_params, actions_params
    const tags = Array.from(
      new Set(
        live_graphs
          .map((graph) => {
            const tagArr = graph.tags || graph.observations || []
            return tagArr.map((tag) => tag.tag)
          })
          .flat(),
      ),
    )

    if (runArr.length > 0 && tags.length > 0) {
      const metric = "runActionsLastUpd"
      actions_params = checkRunInfoUpdate(
        `/warehouse/runs/action_update/?run=${runArr}`,
        metric,
        "actionsUpdatedErr",
        state[metric],
      )

      if (tags.length > 0) {
        const metric = "runTagsLastUpd"
        tags_params = checkRunInfoUpdate(
          `/warehouse/runs/data_update/?tag=${tags}&run=${runArr}`,
          metric,
          "runsUpdatedErr",
          state[metric],
        )
      }
    }

    return tags_params || actions_params ? { ...(tags_params || {}), ...(actions_params || {}) } : null
  }, [])

  const [newActionRuns, setNewActionRuns] = useState([])
  useEffect(() => {
    setNewActionRuns(appConfig.newActionRuns)
  }, [appConfig.newActionRuns])

  const setRunFocus = useCallback((run_obj) => {
    setRun(run_obj)
    setCommentsLoading(true)
    if (run_obj && !run_obj.loading && !run_obj.err) {
      setActions(run_obj.getCombinedActions(run_obj))
      appConfig.setDefaultReactor(run_obj.reactor)
    }
    setCommentsLoading(false)
  }, [])

  const reactorSelectorProps = {
    intl,
    options,
    defaultInfo: runQuery,
    onSuccess: (info) => setRunQuery({ ...info, userLoad: true }),
    disabled: options.length === 0,
  }

  return (
    <>
      {error ? (
        <PageErrorPanel attemptReload={doLoadRuns} />
      ) : firstRunLoadPending || (!didInitialUserQuery && !LIVE_DASH_SELECTIVE_LOADING) ? (
        <PageLoadingPanel fullHeight />
      ) : (
        <>
          {didInitialUserQuery ? (
            <LTPMDash
              intl={intl}
              runQuery={runQuery}
              updateRunQuery={updateRunQuery}
              run={run}
              actions={actions}
              isQueryingRunIDs={isQueryingRunIDs}
              commentsLoading={commentsLoading}
              runs={runs}
              setRunFocus={setRunFocus}
              setCommentsLoading={setCommentsLoading}
              canTogglePlaceholders={false}
              applicationRunsProps={{
                checkForRunUpdate: checkForRunUpdate,
                newActionRuns: newActionRuns, // runs that have had new actions added
                setNewActionRuns: appConfig.setNewActionRuns,
                reactorSelectorProps: reactorSelectorProps,
              }} // LTPM specific props
            />
          ) : (
            <LiveReactorSelector showModal={true} showBtn={false} {...reactorSelectorProps} />
          )}
        </>
      )}
    </>
  )
}

LTPMDashboard.propTypes = {
  queryParams: PropTypes.object.isRequired,
  baseRunidUrl: PropTypes.string.isRequired,
  doFilterDefaultRuns: PropTypes.func.isRequired,
  LIVE_DASH_SELECTIVE_LOADING: PropTypes.bool.isRequired,
}
